// Hands a Time Rule off from the AI chat to the real Add Rule modal in
// Settings → Time Rules. Same shape and reasoning as pendingChatMappingRule.ts
// and pendingChatImport.ts: a module stash plus a DOM event, with a peek that
// does not clear so more than one component can act on the same request.

export type TimeRuleType = 'split' | 'edit' | 'delete';

export interface PendingChatTimeRule {
	// Prefill for a NEW rule, passed to the modal as modalData with
	// isCopy: true — the existing "prefill but still POST" path.
	name?: string;
	description?: string;
	// Decides which tab opens, which is what sets the rule's triggerProcess:
	// AddRuleModal reads its type from modalKey (the open tab), not from its own
	// form, so the tab has to be switched before the modal mounts.
	ruleType?: TimeRuleType;
	// Set instead when the chat resolved an existing rule to edit.
	ruleId?: string;
}

export const CHAT_TIME_RULE_EVENT = 'cap:chat-time-rule-request';

let pending: PendingChatTimeRule | null = null;

export function requestChatTimeRule(request: PendingChatTimeRule) {
	pending = request;
	window.dispatchEvent(new CustomEvent(CHAT_TIME_RULE_EVENT));
}

export function peekChatTimeRule(): PendingChatTimeRule | null {
	return pending;
}

export function consumeChatTimeRule(): PendingChatTimeRule | null {
	if (!pending) {
		return null;
	}
	const request = pending;
	pending = null;
	return request;
}

// The modal reads modalData.criteria and modalData.actions unguarded —
// setCriteria(modalData.criteria), and modalData.actions[0] for an edit rule —
// so a prefill has to carry both or the modal throws on open. These mirror
// AddRuleModal's own useState defaults exactly; a split/delete rule starts with
// two 50% rows, an edit rule with one blank row.
export function buildTimeRulePrefill(request: PendingChatTimeRule) {
	const blankRow = () => ({
		key: Math.random(),
		employeeId: '',
		hours: 50,
		customerId: '',
		customerName: '',
		classId: '',
		className: '',
	});

	return {
		name: request.name || '',
		description: request.description || '',
		isActive: true,
		triggerProcess: request.ruleType || 'split',
		criteria: {
			employeeId: 'ANY',
			classId: '',
			customerId: '',
			operator1: 'AND',
			operator2: 'AND',
		},
		actions:
			request.ruleType === 'edit'
				? [
					{
						key: Math.random().toString(),
						employeeId: '',
						hours: '00:00',
						customerId: '',
						customerName: '',
						classId: '',
						className: '',
					},
				]
				: [blankRow(), blankRow()],
	};
}

// Tab keys in CustomRules/index.tsx: 1 Split, 2 Edit, 3 Delete.
export const TIME_RULE_TAB_KEY: Record<TimeRuleType, string> = {
	split: '1',
	edit: '2',
	delete: '3',
};
